import { Menu } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { ActionShortcutHint } from 'app/modules/wallforming/components/input-binding/ActionShortcutHint';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const Ungroup = () => {
    const { t } = useTranslation();

    const {
        articleInstanceSelections,
        getGroupMemberInstanceIds,
        instanceToGroupMap,
        removeArticleGroup,
        updateArticleInstances
    } = usePlacementStore(
        useShallow((state) => ({
            articleInstanceSelections: state.articleInstanceSelections,
            getGroupMemberInstanceIds: state.getGroupMemberInstanceIds,
            instanceToGroupMap: state.instanceToGroupMap,
            removeArticleGroup: state.removeArticleGroup,
            updateArticleInstances: state.updateArticleInstances
        }))
    );

    const selectedInstanceIds = useMemo(() => Object.keys(articleInstanceSelections), [articleInstanceSelections]);

    const allSelectedInGroup =
        selectedInstanceIds.length > 0 &&
        selectedInstanceIds.every(
            (id) => instanceToGroupMap[id] !== undefined || getGroupMemberInstanceIds(id).length > 1
        );

    const selectedGroupIds = useMemo(() => {
        const groupIds = selectedInstanceIds
            .map((id) => instanceToGroupMap[id])
            .filter((gid): gid is string => gid !== undefined);
        return Array.from(new Set(groupIds));
    }, [selectedInstanceIds, instanceToGroupMap]);

    const handleOnUngroup = useCallback(() => {
        selectedGroupIds.forEach((groupId) => {
            removeArticleGroup(groupId);
        });

        const state = usePlacementStore.getState();
        const solverGroupInstances = selectedInstanceIds
            .map((instanceId) => {
                const articleEntry = Object.entries(state.articles).find(
                    ([, article]) => article.articleInstances[instanceId]
                );
                const articleId = articleEntry?.[0];
                const instance = articleEntry?.[1].articleInstances[instanceId];

                if (!articleId || !instance || state.instanceToGroupMap[instanceId]) {
                    return null;
                }

                if (!instance.sourceGroupInstanceId && !instance.sourceGroupId && !instance.sourceSavedGroupPrefabId) {
                    return null;
                }

                return {
                    articleId,
                    instanceId,
                    instance: {
                        ...instance,
                        sourceType: undefined,
                        sourceGroupId: null,
                        sourceGroupName: null,
                        sourceGroupInstanceId: null,
                        sourceSavedGroupPrefabId: null
                    }
                };
            })
            .filter((entry) => entry !== null);

        if (solverGroupInstances.length > 0) {
            updateArticleInstances(solverGroupInstances, false);
        }
    }, [selectedGroupIds, selectedInstanceIds, removeArticleGroup, updateArticleInstances]);

    return (
        <Menu.Item
            disabled={!allSelectedInGroup}
            leftSection={
                <Icon className="action-menu-entry-decoration action-menu-entry-decoration-icon" icon="stack_off" />
            }
            rightSection={
                <ActionShortcutHint actionId={InputActionId.ArticleUngroup} />
            }
            onClick={handleOnUngroup}
        >
            {t('labelUngroup')}
        </Menu.Item>
    );
};
